"use client";

import { useEffect, useRef, useCallback, useMemo } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import { selectAuthUser } from "../../authPatient/redux/selectors/authSelectors";
import { markReadLocal, pushNotification } from "../redux/notificationsSlice";
import { getNotificationRoute } from "../getNotificationRoute";
import {
  markNotificationRead,
  fetchNotifications,
} from "../redux/notificationsThunks";
import { invalidateMedicalCapabilities } from "../../authorization/useMedicalCapabilities";

/* =========================
 * Cookies
 * ========================= */
function getCookie(name) {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  } catch {}
  return null;
}

function getStoredToken(key) {
  if (typeof window === "undefined") return null;
  return (
    window.sessionStorage?.getItem(key) ||
    window.localStorage?.getItem(key) ||
    null
  );
}

function pickTokenFromCookies(role) {
  const patientToken =
    getCookie("accessToken") ||
    getCookie("auth_token") ||
    getCookie("token") ||
    getStoredToken("patientAccessToken") ||
    getStoredToken("patient_token");

  const doctorToken =
    getCookie("doctorAccessToken") ||
    getCookie("doctor_token") ||
    getStoredToken("doctorAccessToken") ||
    getStoredToken("doctor_token");

  if (role === "doctor") return doctorToken || patientToken;
  return patientToken || doctorToken;
}

function debugAuthSnapshot(role) {
  const c = String(document.cookie || "");
  return {
    hasDoctorAccessToken: /doctorAccessToken=/.test(c),
    hasDoctorToken: /doctor_token=/.test(c),
    hasAccessToken: /accessToken=/.test(c),
    hasAuthToken: /auth_token=/.test(c),
    tokenPicked: !!pickTokenFromCookies(role),
  };
}

/* =========================
 * Helpers
 * ========================= */
function normalizeNotifPayload(payload) {
  if (!payload) return null;

  if (payload?.notification && typeof payload.notification === "object") {
    return payload.notification;
  }

  if (payload?.data?.notification && typeof payload.data.notification === "object") {
    return payload.data.notification;
  }

  if (
    payload?.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data) &&
    (payload.data?.id ||
      payload.data?._id ||
      payload.data?.type ||
      payload.data?.event ||
      payload.data?.title)
  ) {
    return payload.data;
  }

  return payload;
}

function buildSocketDedupeKey(raw) {
  const meta = raw?.metadata || raw?.data || {};
  const id = String(raw?._id || raw?.id || meta?._id || meta?.id || "").trim();
  const event = String(raw?.event || meta?.event || raw?.type || meta?.type || "").trim();
  const createdAt = String(raw?.createdAt || meta?.createdAt || "").trim();
  const fromUserId = String(meta?.fromUserId || raw?.fromUserId || meta?.senderId || raw?.senderId || "").trim();
  const targetId = String(meta?.targetId || raw?.targetId || meta?.conversationId || raw?.conversationId || "").trim();
  const messageId = String(meta?.messageId || raw?.messageId || meta?.commentId || raw?.commentId || "").trim();
  const title = String(raw?.title || meta?.title || raw?.messageTitle || "").trim();
  const body = String(raw?.body || raw?.message || meta?.body || meta?.message || "").trim();

  const key = [
    id || "no-id",
    event || "no-event",
    createdAt || "no-created-at",
    fromUserId || "no-user",
    targetId || "no-target",
    messageId || "no-message",
    title || "no-title",
    body || "no-body",
  ].join(":");

  return key;
}

function normId(v) {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  return s;
}

function collectIds(entity) {
  if (!entity) return [];
  const ids = [
    entity?.id,
    entity?._id,
    entity?.userId,
    entity?.pgId,
    entity?.uuid,
    entity?.doctorId,
    entity?.patientId,
    entity?.user?.id,
    entity?.user?._id,
    entity?.user?.userId,
    entity?.user?.pgId,
    entity?.user?.uuid,
  ]
    .map(normId)
    .filter(Boolean);

  const numeric = ids
    .map((x) => {
      const n = Number(x);
      return Number.isFinite(n) ? String(n) : null;
    })
    .filter(Boolean);

  return Array.from(new Set([...ids, ...numeric]));
}

function pickTokenForRole(role) {
  if (role === "patient") {
    return (
      getCookie("accessToken") ||
      getCookie("auth_token") ||
      getCookie("token") ||
      getCookie("patientAccessToken") ||
      getCookie("patient_token") ||
      getStoredToken("patientAccessToken") ||
      getStoredToken("patient_token") ||
      null
    );
  }

  return (
    getCookie("doctorAccessToken") ||
    getCookie("doctor_token") ||
    getCookie("accessToken") ||
    getCookie("auth_token") ||
    getCookie("token") ||
    getStoredToken("doctorAccessToken") ||
    getStoredToken("doctor_token") ||
    null
  );
}

function resolveFullName(entity) {
  if (!entity) return null;

  const full =
    (entity?.fullName && String(entity.fullName).trim()) ||
    `${entity?.firstName || ""} ${entity?.lastName || ""}`.trim() ||
    `${entity?.prenom || ""} ${entity?.nom || ""}`.trim() ||
    (entity?.name && String(entity.name).trim()) ||
    (entity?.displayName && String(entity.displayName).trim()) ||
    (entity?.username && String(entity.username).trim()) ||
    (entity?.email && String(entity.email).trim());

  const full2 =
    full ||
    (entity?.user?.fullName && String(entity.user.fullName).trim()) ||
    `${entity?.user?.firstName || ""} ${entity?.user?.lastName || ""}`.trim() ||
    `${entity?.user?.prenom || ""} ${entity?.user?.nom || ""}`.trim() ||
    (entity?.user?.name && String(entity.user.name).trim()) ||
    (entity?.user?.displayName && String(entity.user.displayName).trim()) ||
    (entity?.user?.username && String(entity.user.username).trim()) ||
    (entity?.user?.email && String(entity.user.email).trim());

  return full2 || null;
}

function findEntityById(list, wantedId) {
  const wid = normId(wantedId);
  if (!wid) return null;

  const widNum = String(Number(wid));
  const wanted = new Set([wid, widNum].filter((x) => x && x !== "NaN"));

  const arr = Array.isArray(list) ? list : [];
  return (
    arr.find((e) => {
      const ids = collectIds(e);
      return ids.some((cid) => wanted.has(cid));
    }) || null
  );
}

function buildSelectedContact({
  role,
  peerRole,
  fromUserId,
  conversationId,
  doctorsList,
  patientsList,
  fallbackName,
}) {
  const peerId = normId(fromUserId);
  if (!peerId) return null;

  const isDoctorMode = role === "doctor";
  const peerType =
    peerRole === "doctor" || peerRole === "patient"
      ? peerRole
      : isDoctorMode
        ? "patient"
        : "doctor";

  const peerEntity =
    peerType === "doctor"
      ? findEntityById(doctorsList, peerId)
      : findEntityById(patientsList, peerId);

  const name =
    resolveFullName(peerEntity) ||
    fallbackName ||
    (peerType === "doctor" ? "Doctor" : "Patient");

  const avatar =
    peerEntity?.avatarUrl ||
    peerEntity?.avatar ||
    peerEntity?.photo ||
    peerEntity?.image ||
    (peerType === "doctor"
      ? "https://cdn-icons-png.flaticon.com/512/3774/3774299.png"
      : "https://cdn-icons-png.flaticon.com/512/149/149071.png");

  const specialty = peerEntity?.specialty || peerEntity?.role || "";

  return {
    id: peerId,
    userId: peerId,
    peerId: peerId,
    fullName: name,
    name: name,
    avatar: avatar,
    photo: avatar,
    email: peerEntity?.email || "",
    specialty: peerType === "doctor" ? specialty || "General Practitioner" : undefined,
    role: peerType,
    type: peerType,
    isDoctor: peerType === "doctor",
    meta: {
      type: peerType,
      role: peerType,
      registerId: peerId,
      patientId: peerType === "patient" ? peerId : undefined,
      doctorId: peerType === "doctor" ? peerId : undefined,
      conversationId: conversationId || undefined,
      displayName: name,
      specialty: peerType === "doctor" ? specialty : undefined,
      email: peerEntity?.email || "",
    },
  };
}

export default function useNotificationsSocket({
  enabled = true,
  roleOverride,
  userIdOverride,
  onNotification,
} = {}) {
  const socketRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const doctorsList = useSelector((state) => {
    const s = state.doctors || state.doctorRegister || state.doctor || {};
    return s.items || s.data || s.doctors || s.list || [];
  });

  const patientsList = useSelector((state) => {
    const s = state.user || {};
    return s.data || s.users || s.items || [];
  });

  const doctorUser = useSelector((state) => state.doctor?.user || state.doctor?.currentUser || null);
  const patientUser = useSelector(selectAuthUser);
  const effectiveUserId = useSelector((state) => selectEffectiveUserId(state));
  const latestNotificationCreatedAt = useSelector((state) => {
    const latest = state.notifications?.items?.[0];
    return latest?.createdAt || latest?._raw?.createdAt || null;
  });
  const connectedUserRef = useRef(null);
  const latestNotificationCreatedAtRef = useRef(latestNotificationCreatedAt);
  latestNotificationCreatedAtRef.current = latestNotificationCreatedAt;

  const role = useMemo(() => {
    if (roleOverride === "doctor" || roleOverride === "patient") return roleOverride;
    if (doctorUser) return "doctor";
    if (patientUser) return "patient";
    const hasDoctorToken = !!(getCookie("doctorAccessToken") || getCookie("doctor_token"));
    return hasDoctorToken ? "doctor" : "patient";
  }, [roleOverride, doctorUser, patientUser]);

  const currentUserId = useMemo(() => {
    const overrideId = normId(userIdOverride);
    if (overrideId) return overrideId;

    const candidates =
      role === "doctor"
        ? [doctorUser?.id, doctorUser?.userId, doctorUser?.uuid, doctorUser?.pgId, doctorUser?.doctorId]
        : [effectiveUserId, patientUser?.id, patientUser?.userId, patientUser?.uuid, patientUser?.pgId];

    for (const candidate of candidates) {
      const id = normId(candidate);
      if (id) return id;
    }

    return null;
  }, [role, doctorUser, patientUser, effectiveUserId, userIdOverride]);

  const activeSubprofileId = useMemo(() => {
    if (role !== "patient" || !effectiveUserId || !patientUser) return null;
    const principalId = normId(
      patientUser?.id || patientUser?.userId || patientUser?.uuid,
    );
    const effectiveId = normId(effectiveUserId);
    return effectiveId && effectiveId !== principalId ? effectiveId : null;
  }, [role, effectiveUserId, patientUser]);

  const seenRef = useRef(new Map());
  const rememberSeen = useCallback((key, ttlMs = 12000) => {
    const now = Date.now();
    for (const [k, ts] of seenRef.current.entries()) {
      if (now - ts > ttlMs) seenRef.current.delete(k);
    }
    if (seenRef.current.has(key)) return true;
    seenRef.current.set(key, now);
    return false;
  }, []);

  const handleNotificationClick = useCallback(
    async (notif) => {
      if (!notif) return;

      const notifId = String(notif?._id || notif?.id || "").trim();
      if (notifId) {
        dispatch(markReadLocal(notifId));
        try {
          await dispatch(markNotificationRead({ id: notifId })).unwrap();
        } catch (error) {
          console.error("[NOTIF-WS] Failed to mark notification read", error);
          message.warning("Could not sync the read status. It will be retried.");
        }
      }

      const meta = notif?.metadata || notif?.data || {};
      const event = String(notif?.event || "").toLowerCase();
      const isMessage =
        String(notif?.type || "").toUpperCase() === "MESSAGE" ||
        event.includes("chat.message") ||
        event.includes("message");
      const fromId = normId(
        meta?.fromUserId || notif?.fromUserId || meta?.senderId || null
      );
      const isDoctorConversation =
        role === "doctor" &&
        (String(meta?.conversationType || "").toUpperCase() === "DOCTOR_DOCTOR" ||
          String(meta?.fromRole || meta?.senderRole || "").toLowerCase() === "doctor");

      if (isMessage && fromId) {
        const selectedContact = buildSelectedContact({
          role,
          peerRole: isDoctorConversation ? "doctor" : undefined,
          fromUserId: fromId,
          conversationId: meta?.conversationId,
          doctorsList,
          patientsList,
          fallbackName:
            notif?.fromUserName ||
            notif?.senderName ||
            meta?.fromUserName ||
            meta?.senderName ||
            null,
        });
        if (selectedContact) {
          sessionStorage.setItem("pendingSelectedContact", JSON.stringify(selectedContact));
          sessionStorage.setItem("autoSelectContactId", String(fromId));
        }
      }

      const route = getNotificationRoute(notif, role);
      if (!route) {
        message.info("This notification has no destination available.");
        return;
      }

      return navigate(route, {
        state: {
          fromNotif: true,
          notification: notif,
          ...(isMessage
            ? {
                selectedContact: fromId
                  ? buildSelectedContact({
                      role,
                      peerRole: isDoctorConversation ? "doctor" : undefined,
                      fromUserId: fromId,
                      conversationId: meta?.conversationId,
                      doctorsList,
                      patientsList,
                      fallbackName: meta?.fromUserName || meta?.senderName,
                    })
                  : null,
                autoOpen: true,
                messageId: meta?.messageId || notif?.messageId || null,
              }
            : {}),
        },
      });
    },
    [dispatch, navigate, role, doctorsList, patientsList]
  );

  useEffect(() => {
    if (!enabled) return;

    if (socketRef.current) {
      try {
        socketRef.current.off();
        socketRef.current.disconnect();
      } catch {}
      socketRef.current = null;
    }

    const rawBase =
      import.meta.env.VITE_SOCKET_URL ||
      import.meta.env.VITE_GATEWAY_URL ||
      window.location.origin;
    const baseUrl = String(rawBase).replace(/\/+$/, "");
    const normalizedBase = baseUrl.replace(/\/ws\/.*$/i, "").replace(/\/api$/i, "");
    const namespace = String(import.meta.env.VITE_SOCKET_NAMESPACE || "/ws/notifications").replace(/\/+$/, "");
    const wsUrl = `${normalizedBase}${namespace}`;

    const token = pickTokenForRole(role) || pickTokenFromCookies(role);

    const socket = io(wsUrl, {
      path: import.meta.env.VITE_SOCKET_PATH || "/socket.io",
      transports: ["websocket", "polling"],
      withCredentials: true,
      auth: token
        ? { token, ...(activeSubprofileId ? { subprofileId: activeSubprofileId } : {}) }
        : {},
      extraHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      reconnection: true,
      reconnectionAttempts: 50,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      timeout: 15000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.info("✅ [NOTIF-WS] connected", { id: socket.id, url: wsUrl, role });
      const since =
        connectedUserRef.current === currentUserId
          ? latestNotificationCreatedAtRef.current
          : null;
      connectedUserRef.current = currentUserId;
      if (currentUserId) {
        dispatch(
          fetchNotifications({
            skip: 0,
            limit: 100,
            userId: currentUserId,
            role,
            ...(since ? { since } : {}),
          })
        );
      }
    });

    socket.on("connect_error", (err) => {
      console.error("❌ [NOTIF-WS] connect_error:", err?.message || String(err));
    });

    socket.io.on("reconnect_attempt", (attempt) => {
      console.info("[NOTIF-WS] reconnect_attempt", { attempt, url: wsUrl });
    });
    socket.io.on("reconnect", (attempt) => {
      console.info("[NOTIF-WS] reconnect", { attempt, id: socket.id });
    });

    socket.on("connection_confirmed", (data) => {
      console.info("✅ [NOTIF-WS] connection_confirmed", data);
    });

    socket.on("auth_error", (data) => {
      console.error("[NOTIF-WS] auth_error", data?.message || "Authentication failed");
    });
    const requestTestNotification = (event) => {
      const targetUserId = event.detail?.userId || currentUserId;
      if (!targetUserId) {
        console.warn("[NOTIF-WS] Cannot test notification without a signed-in user.");
        return;
      }
      socket.emit("notification:test", { userId: String(targetUserId) });
    };
    const onTestNotificationResult = (result) => {
      console.info("[NOTIF-WS] notification:test:result", result);
    };
    window.addEventListener(
      "mediwave:notification:test",
      requestTestNotification
    );
    socket.on("notification:test:result", onTestNotificationResult);

    socket.on("disconnect", (reason) => {
      console.warn("[NOTIF-WS] disconnect", { reason });
    });

    const onNotif = (payload, eventName = "unknown") => {

      const raw = normalizeNotifPayload(payload);
      if (!raw) {
        return;
      }

      const meta = raw?.metadata || raw?.data || {};
      const accessEvent = String(raw?.event || '').toLowerCase();
      if (
        accessEvent === 'patient.data_access.responded' ||
        accessEvent === 'patient.data_access.revoked'
      ) {
        invalidateMedicalCapabilities(meta?.patientId, meta?.doctorId);
      }

      const dedupeKey = buildSocketDedupeKey(raw);
      if (rememberSeen(dedupeKey)) {
        return;
      }

      dispatch(pushNotification(raw));
      try {
        onNotification?.(raw);
      } catch (error) {
        console.error("[NOTIF-WS] Notification callback failed", error);
      }

    };

    socket.on("notification", (payload) => onNotif(payload, "notification"));
    socket.on("notif:new", (payload) => onNotif(payload, "notif:new"));
    socket.on("notification.created", (payload) => onNotif(payload, "notification.created"));

    return () => {
      try {
        socket.off("notification");
        socket.off("notif:new");
        socket.off("notification.created");
        socket.off("connection_confirmed");
        socket.off("auth_error");
        socket.off("notification:test:result", onTestNotificationResult);
        socket.off("disconnect");
        socket.off("connect");
        socket.io.off("reconnect_attempt");
        socket.io.off("reconnect");
        window.removeEventListener(
          "mediwave:notification:test",
          requestTestNotification
        );
        socket.disconnect();
      } catch {}
      socketRef.current = null;
    };
  }, [
    enabled,
    dispatch,
    rememberSeen,
    role,
    currentUserId,
    activeSubprofileId,
    onNotification,
  ]);
  // doctorUser/patientUser deliberately excluded: only used here via the
  // already-memoized role/currentUserId. getCurrentDoctor reassigns
  // state.doctor.user to a new object on every fetch (dispatched from many
  // pages) — including the raw objects tore the socket down and rebuilt it
  // on nearly every navigation.

  return { socketRef, handleNotificationClick };
}
